import {Component, computed, effect, inject, signal} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {forkJoin, of} from 'rxjs';
import {catchError, finalize, switchMap} from 'rxjs/operators';

import {TranslocoDirective, TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {
  CompanyOfficeDetailResponse,
  CustomerUserControllerService,
  CustomerUserResponse,
  ModuleFunctionControllerService,
  ProjectControllerService,
  ProjectDetailResponse,
  ProjectUserControllerService,
  ProjectUserDetailResponse,
} from '@frontend/shared-api';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {ProjectListComponent} from './list/project-list.component';
import {ToastService} from '@frontend/toast';
import {ToggleSwitch} from 'primeng/toggleswitch';
import {OfficeListService} from '../../../../shared/components/office-list/office-list.service';
import {BackgroundCardComponent} from '../../../../shared/components/titled-background-card/background-card.component';
import {RoleCheckService} from '../../../../shared/services/role-check.service';
import {APP_ROUTES} from '../../../../app.routes';
import {LoadingComponent} from '../../../../shared/components/loading/loading.component';
import {ProjectCopyDialogComponent} from './list/components/project-copy-dialog.component';

@Component({
  selector: 'app-project-overview',
  templateUrl: './project-overview.component.html',
  imports: [
    TranslocoDirective,
    FormsModule,
    Button,
    ProjectListComponent,
    ToggleSwitch,
    BackgroundCardComponent,
    TranslocoPipe,
    LoadingComponent,
    ProjectCopyDialogComponent,
  ],
})
export class ProjectOverviewComponent {
  readonly offices = signal<CompanyOfficeDetailResponse[]>([]);
  readonly projects = signal<ProjectDetailResponse[]>([]);
  readonly projectUserDetails = signal<Map<number, ProjectUserDetailResponse>>(new Map());
  readonly currentUser = signal<CustomerUserResponse>(null as unknown as CustomerUserResponse);
  readonly loading = signal(false);
  readonly showArchived = signal(false);
  readonly copyDialogVisible = signal(false);
  readonly projectIdToCopy = signal<number | null>(null);
  readonly hasArchived = computed(() => this.projects().some((p) => !!p.archivedAt));
  readonly hasModuleFunctions = signal(false);
  readonly displayedProjects = computed(() =>
    this.projects().filter((p) => this.showArchived() || !p.archivedAt),
  );
  protected readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  protected readonly roleCheckService = inject(RoleCheckService);
  private readonly moduleFunctionControllerService = inject(ModuleFunctionControllerService);

  readonly emptyState = computed(() => {
    if (this.offices().length === 0) {
      return {
        testId: 'empty-offices-list',
        title: this.transloco.translate('company.emptyListText'),
        buttonLabel: this.roleCheckService.hasCustomerAdminRole()
          ? this.transloco.translate('company.setup')
          : '',
        buttonTestId: 'setup-company-empty-button',
        clickHandler: () => this.router.navigate([APP_ROUTES.COMPANY + APP_ROUTES.CREATE]),
      };
    }
    if (this.displayedProjects().length === 0) {
      return {
        testId: 'empty-project-list',
        title: this.transloco.translate('project.emptyListText'),
        buttonLabel: this.roleCheckService.hasCustomerAdminRole()
          ? this.transloco.translate('project.setup')
          : '',
        buttonTestId: 'setup-project-empty-button',
        clickHandler: () => this.router.navigate([APP_ROUTES.PROJECT + APP_ROUTES.CREATE]),
      };
    }
    return null;
  });

  private readonly toast = inject(ToastService);
  private readonly companyListService = inject(OfficeListService);
  private readonly projectController = inject(ProjectControllerService);
  private readonly projectUserController = inject(ProjectUserControllerService);
  private readonly userController = inject(CustomerUserControllerService);
  private readonly refreshTrigger = signal(0);

  constructor() {
    effect(() => {
      this.refreshTrigger();
      this.loadData();
    });
  }

  private loadData(): void {
    this.loadProjectsAndOfficesWithUserDetails();
    this.loadHasModuleFunctions();
  }

  private loadProjectsAndOfficesWithUserDetails(): void {
    this.loading.set(true);

    this.userController
      .getCurrentCustomerUser()
      .pipe(
        switchMap((currentUser) =>
          forkJoin({
            offices: this.companyListService
              .getAllOfficesWithDetails()
              .pipe(catchError(() => of([]))),
            projects: this.projectController.getAllProjects().pipe(catchError(() => of([]))),
            currentUser: of(currentUser),
          }),
        ),
        switchMap(({offices, projects, currentUser}) => {
          this.offices.set(offices);
          this.projects.set(projects);
          this.currentUser.set(currentUser);

          const projectUserDetailsRequests = projects.map((project) =>
            this.projectUserController
              .getProjectUserWithDetails({
                projectId: project.id,
                userId: currentUser.id,
              })
              .pipe(catchError(() => of(null))),
          );

          return forkJoin({
            projects: of(projects),
            projectUserDetails: forkJoin(projectUserDetailsRequests),
            currentUser: of(currentUser),
          });
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe(({projects, projectUserDetails}) => {
        const userDetailsMap = new Map<number, ProjectUserDetailResponse>();
        projects.forEach((project, index) => {
          const userDetail = projectUserDetails[index];
          if (userDetail) {
            userDetailsMap.set(project.id, userDetail);
          }
        });
        this.projectUserDetails.set(userDetailsMap);
      });
  }

  goToProject(id: number) {
    this.router.navigate([APP_ROUTES.PROJECT, id]);
  }

  onChildEdit(id: number) {
    this.router.navigate([APP_ROUTES.PROJECT, id, APP_ROUTES.EDIT]).then(() => {
      const sub = this.router.events.subscribe((e) => {
        if (e instanceof NavigationEnd) {
          this.requestRefresh();
          sub.unsubscribe();
        }
      });
    });
  }

  onChildArchive(id: number) {
    this.projectController
      .archiveProject({id})
      .pipe(finalize(() => this.requestRefresh()))
      .subscribe({
        next: () => {
          this.toast.showSuccess(
            'project.card.archive.success.header',
            'project.card.archive.success.detail',
          );
        },
      });
  }

  onChildDelete(id: number) {
    this.projectController
      .deleteProject({id})
      .pipe(finalize(() => this.requestRefresh()))
      .subscribe({
        next: () => {
          this.toast.showSuccess(
            'project.card.delete.success.header',
            'project.card.delete.success.detail',
          );
        },
      });
  }

  onChildCopy(id: number) {
    this.projectIdToCopy.set(id);
    this.copyDialogVisible.set(true);
  }

  onCopyConfirmed(name: string) {
    const id = this.projectIdToCopy();
    if (id === null) {
      return;
    }

    this.projectController
      .copyProject({id, copyProjectRequest: {name}})
      .pipe(finalize(() => this.requestRefresh()))
      .subscribe({
        next: () => {
          this.toast.showSuccess(
            'project.card.copy.success.header',
            'project.card.copy.success.detail',
          );
        },
      });
  }

  onInvitationResponse(projectId: number, accept: boolean) {
    this.projectUserController
      .respondInvitation({
        projectId,
        invitationActionRequest: {accept},
      })
      .pipe(finalize(() => this.requestRefresh()))
      .subscribe({
        next: () => {
          const messageKey = accept ? 'accepted' : 'rejected';
          this.toast.showSuccess(
            `project.invitation.${messageKey}.success.header`,
            `project.invitation.${messageKey}.success.detail`,
          );
        },
      });
  }

  private requestRefresh() {
    this.refreshTrigger.update((n) => n + 1);
  }

  isContractActive(): boolean {
    return this.currentUser()?.hasActiveContract;
  }

  protected readonly APP_ROUTES = APP_ROUTES;

  private loadHasModuleFunctions() {
    this.moduleFunctionControllerService.hasAnyModuleFunctions().subscribe((result) => {
      this.hasModuleFunctions.set(result);
    });
  }
}
